// src/components/TaskManagement.js

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { usePermissions } from '../App'; // Import the permissions hook
import { useConfirm } from './ConfirmProvider';

function TaskManagement({
  cases,
  tasks,
  users,
  paralegalsList,
  userRole,
  authAxios,
  fetchTasks,
  showFormMessage,
  handleDelete,
  currentUser,
  taskStatusOptions,
  taskPriorityOptions,
  navigateTo,
  setNavigateTo
}) {
  const permissions = usePermissions(); // Use the permissions hook
  const confirm = useConfirm();
  const canSeeAssignmentScoping = permissions.isPartner || permissions.isAssociate || permissions.isSuperuser;
  const [showAddTaskForm, setShowAddTaskForm] = useState(false);
  const [tasksViewMode, setTasksViewMode] = useState('board'); // Default to board view
  const [newTask, setNewTask] = useState({
    case: '', title: '', description: '', due_date: '', status: 'To-Do', priority: 'Low', assigned_to: '',
  });
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const editingTaskOriginalStatusRef = useRef(null);
  const [showTaskDetailsModal, setShowTaskDetailsModal] = useState(false);
  const [selectedTaskDetails, setSelectedTaskDetails] = useState(null);

  // Search, Filter, and Sort States
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [assignmentFilter, setAssignmentFilter] = useState(''); // '', 'to_me', 'by_me'
  const [sortConfig, setSortConfig] = useState({ key: 'due_date', direction: 'ascending' });

  // Team task-oversight mode (Partner/Associate/Superuser only): per-employee
  // breakdown of tasks the current user has assigned to others, with drill-down.
  const [showOversightMode, setShowOversightMode] = useState(false);
  const [oversightData, setOversightData] = useState(null);
  const [oversightLoading, setOversightLoading] = useState(false);
  const [oversightSelectedEmployee, setOversightSelectedEmployee] = useState(null);
  const [oversightEmployeeTasks, setOversightEmployeeTasks] = useState([]);
  const [oversightTasksLoading, setOversightTasksLoading] = useState(false);

  // Restore saved view preferences from localStorage
  useEffect(() => {
    try {
      const p = JSON.parse(localStorage.getItem('tasks.ui') || '{}');
      if (p.viewMode) setTasksViewMode(p.viewMode);
      if (typeof p.searchTerm === 'string') setSearchTerm(p.searchTerm);
      if (typeof p.statusFilter === 'string') setStatusFilter(p.statusFilter);
      if (typeof p.priorityFilter === 'string') setPriorityFilter(p.priorityFilter);
      if (typeof p.assignmentFilter === 'string') setAssignmentFilter(p.assignmentFilter);
      if (p.sortConfig && p.sortConfig.key) setSortConfig(p.sortConfig);
    } catch (_) {}
  }, []);
  useEffect(() => {
    try { localStorage.setItem('tasks.ui', JSON.stringify({ viewMode: tasksViewMode, searchTerm, statusFilter, priorityFilter, assignmentFilter, sortConfig })); } catch (_) {}
  }, [tasksViewMode, searchTerm, statusFilter, priorityFilter, assignmentFilter, sortConfig]);

  const filteredAndSortedTasks = useMemo(() => {
    let filteredTasks = Array.isArray(tasks) ? [...tasks] : [];

    if (searchTerm) {
      filteredTasks = filteredTasks.filter(task =>
        task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (task.description && task.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    if (statusFilter) {
      filteredTasks = filteredTasks.filter(task => task.status === statusFilter);
    }
    if (priorityFilter) {
      filteredTasks = filteredTasks.filter(task => task.priority === priorityFilter);
    }
    if (assignmentFilter && currentUser) {
      filteredTasks = filteredTasks.filter(task => {
        if (assignmentFilter === 'to_me') {
          const assignedId = task.assigned_to?.id ?? task.assigned_to;
          return assignedId === currentUser.id;
        }
        if (assignmentFilter === 'by_me') {
          const createdId = task.created_by?.id ?? task.created_by;
          return createdId === currentUser.id;
        }
        return true;
      });
    }

    filteredTasks.sort((a, b) => {
      if (!a[sortConfig.key] || !b[sortConfig.key]) return 0;

      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });

    return filteredTasks;
  }, [tasks, searchTerm, statusFilter, priorityFilter, assignmentFilter, currentUser, sortConfig]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handleTaskInputChange = (e) => {
    const { name, value } = e.target;
    setNewTask({ ...newTask, [name]: value });
  };

  const handleTaskSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    try {
      const dataToSend = { ...newTask, assigned_to: newTask.assigned_to || null };
      await authAxios.post('tasks/', dataToSend);
      if(showFormMessage) showFormMessage('success', 'Task added successfully!');
      setShowAddTaskForm(false);
      setNewTask({
        case: '', title: '', description: '', due_date: '', status: 'To-Do', priority: 'Low', assigned_to: '',
      });
      if(fetchTasks) fetchTasks();
    } catch (err) {
      console.error('Error adding Task:', err.response ? err.response.data : err.message);
      if(showFormMessage) showFormMessage('danger', `Failed to add Task. ${err.response?.data ? JSON.stringify(err.response.data) : ''}`);
    }
  }, [authAxios, newTask, fetchTasks, showFormMessage]);

  const handleEditingTaskChange = (e) => {
    const { name, value } = e.target;
    setEditingTask(prev => ({ ...prev, [name]: value }));
  };

  const handleEditTaskSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!editingTask) return;
    try {
      // Ensure we are sending the ID, not the object, for foreign keys
      const payload = {
          ...editingTask,
          case: editingTask.case?.id || editingTask.case,
          assigned_to: editingTask.assigned_to?.id || editingTask.assigned_to,
      };

      const isNewlyCompleted = editingTask.status === 'Completed' && editingTaskOriginalStatusRef.current !== 'Completed';
      const isOverdue = editingTask.due_date && new Date() > new Date(editingTask.due_date);
      if (isNewlyCompleted && isOverdue) {
        const reason = await confirm({
          title: 'Reason for delay',
          message: `"${editingTask.title}" is past its due date. Please provide a reason for completing it late.`,
          confirmText: 'Complete Task',
          promptMode: true,
          promptLabel: 'Reason for completing this task late',
          required: true,
        });
        if (!reason) return; // Cancelled - abort the save entirely.
        payload.delay_reason = reason;
      }

      await authAxios.patch(`tasks/${editingTask.id}/`, payload);
      if(showFormMessage) showFormMessage('success', 'Task updated successfully!');
      setShowEditTaskModal(false);
      setEditingTask(null);
      if(fetchTasks) fetchTasks();
    } catch (err) {
      console.error('Error updating Task:', err.response ? err.response.data : err.message);
      if(showFormMessage) showFormMessage('danger', `Failed to update Task. ${err.response?.data ? JSON.stringify(err.response.data) : ''}`);
    }
  }, [editingTask, authAxios, fetchTasks, showFormMessage, confirm]);
    
  // Drag and Drop Handlers for Kanban Board
  const onDragStart = (e, taskId) => {
      e.dataTransfer.setData("taskId", taskId);
  };

  const onDragOver = (e) => {
      e.preventDefault();
  };

  const onDrop = async (e, newStatus) => {
      if (!permissions.canEditTask) {
        if(showFormMessage) showFormMessage('danger', 'You do not have permission to change the task status.');
        return;
      }
      const taskId = e.dataTransfer.getData("taskId");
      const taskToUpdate = tasks.find(t => t.id.toString() === taskId);

      if (taskToUpdate && taskToUpdate.status !== newStatus) {
          const payload = { status: newStatus };
          if (newStatus === 'Completed' && taskToUpdate.due_date && new Date() > new Date(taskToUpdate.due_date)) {
            const reason = await confirm({
              title: 'Reason for delay',
              message: `"${taskToUpdate.title}" is past its due date. Please provide a reason for completing it late.`,
              confirmText: 'Complete Task',
              promptMode: true,
              promptLabel: 'Reason for completing this task late',
              required: true,
            });
            if (!reason) return; // Cancelled - abort the drop, task stays where it was.
            payload.delay_reason = reason;
          }
          try {
              await authAxios.patch(`tasks/${taskId}/`, payload);
              if(showFormMessage) showFormMessage('success', `Task status updated to ${newStatus}`);
              if(fetchTasks) fetchTasks();
          } catch (err) {
              console.error('Error updating task status:', err.response ? err.response.data : err.message);
              if(showFormMessage) showFormMessage('danger', 'Failed to update task status.');
          }
      }
  };

  // --- Team task-oversight mode: per-employee breakdown of tasks the
  // current user assigned to others, with drill-down into the task list. ---
  const loadOversightData = useCallback(async () => {
    setOversightLoading(true);
    try {
      const resp = await authAxios.get('performance-goals/task-turnaround/', { params: { created_by: 'me' } });
      setOversightData(resp.data);
    } catch (err) {
      console.error('Error loading task oversight data:', err.response ? err.response.data : err.message);
      if (showFormMessage) showFormMessage('danger', 'Failed to load task oversight data.');
    } finally {
      setOversightLoading(false);
    }
  }, [authAxios, showFormMessage]);

  useEffect(() => {
    if (showOversightMode) {
      loadOversightData();
      setOversightSelectedEmployee(null);
      setOversightEmployeeTasks([]);
    }
  }, [showOversightMode, loadOversightData]);

  const selectOversightEmployee = useCallback(async (employee) => {
    setOversightSelectedEmployee(employee);
    setOversightTasksLoading(true);
    try {
      const resp = await authAxios.get('tasks/', {
        params: { created_by__id: currentUser?.id, assigned_to__id: employee.user_id },
      });
      const results = Array.isArray(resp.data) ? resp.data : (resp.data?.results || []);
      setOversightEmployeeTasks(results);
    } catch (err) {
      console.error('Error loading employee tasks:', err.response ? err.response.data : err.message);
      if (showFormMessage) showFormMessage('danger', 'Failed to load tasks for this employee.');
    } finally {
      setOversightTasksLoading(false);
    }
  }, [authAxios, currentUser, showFormMessage]);

  const toLocalInputValue = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const openEditTaskModal = useCallback((task) => {
    editingTaskOriginalStatusRef.current = task.status;
    setEditingTask({
      ...task,
      due_date: toLocalInputValue(task.due_date),
      case: task.case?.id || task.case,
      assigned_to: task.assigned_to?.id || task.assigned_to,
    });
    setShowEditTaskModal(true);
    setShowTaskDetailsModal(false); // Close details modal if open
  }, []);

  const closeEditTaskModal = useCallback(() => {
    setEditingTask(null);
    setShowEditTaskModal(false);
  }, []);

  const openTaskDetailsModal = useCallback((task) => {
    setSelectedTaskDetails(task);
    setShowTaskDetailsModal(true);
  }, []);

  const closeTaskDetailsModal = useCallback(() => {
    setSelectedTaskDetails(null);
    setShowTaskDetailsModal(false);
  }, []);

  useEffect(() => {
    if (navigateTo && navigateTo.tab === 'tasks') {
      const task = tasks.find(t => t.id === navigateTo.id);
      if (task) {
        openTaskDetailsModal(task);
        if(setNavigateTo) setNavigateTo(null); // Reset after navigation
      }
    }
  }, [navigateTo, tasks, openTaskDetailsModal, setNavigateTo]);

  // Auto-open via custom event (open-resource-detail)
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail;
      if (!detail || detail.tab !== 'tasks') return;
      const task = tasks.find(t => t.id === detail.id);
      if (task) {
        openTaskDetailsModal(task);
        if (setNavigateTo) setNavigateTo(null);
      }
    };
    window.addEventListener('open-resource-detail', handler);
    return () => window.removeEventListener('open-resource-detail', handler);
  }, [tasks, openTaskDetailsModal, setNavigateTo]);
  
  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'Urgent': return 'red';
      case 'High': return 'yellow';
      case 'Medium': return 'blue';
      case 'Low':
      default: return 'gray';
    }
  }

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Africa/Gaborone'
    });
  };

  const allowedUsers = useMemo(() => {
    // Build a unified candidate list ensuring associates see themselves + all users returned by API (paralegals & support staff)
    const staffUsers = Array.isArray(users) ? users : [];
    const paras = Array.isArray(paralegalsList) ? paralegalsList : [];

    const map = new Map();
    if (userRole === 'ASSOCIATE') {
      // Include all staff users provided by API (backend limits to self + paralegals + support staff)
      for (const u of staffUsers) map.set(u.id, u);
      // Include paralegals list explicitly (covers cases where users API is restricted)
      for (const u of paras) map.set(u.id, u);
      // Always include current associate
      if (currentUser && currentUser.id) {
        map.set(currentUser.id, { id: currentUser.id, username: currentUser.username || 'You', is_staff: true });
      }
      return Array.from(map.values());
    }
    // For non-associates, show all users by default
    for (const u of staffUsers) map.set(u.id, u);
    return Array.from(map.values());
  }, [users, paralegalsList, userRole, currentUser]);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <h2 className="text-3xl font-bold text-gray-800 mb-6">Task Management</h2>
      <div className="flex justify-between items-center mb-6">
        {permissions.canCreateTask && (
        <button className="bg-blue-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center" onClick={() => setShowAddTaskForm(!showAddTaskForm)}>
          {showAddTaskForm ? 'Cancel' : (
              <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  New Task
              </>
          )}
        </button>
        )}
        <div className="flex space-x-2">
          <button className={`px-4 py-2 rounded-md text-sm font-medium ${!showOversightMode && tasksViewMode === 'board' ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'}`} onClick={() => { setShowOversightMode(false); setTasksViewMode('board'); }}>Board</button>
          <button className={`px-4 py-2 rounded-md text-sm font-medium ${!showOversightMode && tasksViewMode === 'list' ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'}`} onClick={() => { setShowOversightMode(false); setTasksViewMode('list'); }}>List</button>
          <button className={`px-4 py-2 rounded-md text-sm font-medium ${!showOversightMode && tasksViewMode === 'tiles' ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'}`} onClick={() => { setShowOversightMode(false); setTasksViewMode('tiles'); }}>Tiles</button>
          {canSeeAssignmentScoping && (
            <button className={`px-4 py-2 rounded-md text-sm font-medium ${showOversightMode ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'}`} onClick={() => setShowOversightMode(true)}>Team Oversight</button>
          )}
        </div>
      </div>

      {showAddTaskForm && (
        <div className={`fixed inset-0 bg-gray-600 bg-opacity-75 h-full w-full z-50 transition-opacity duration-300 ${showAddTaskForm ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
          <div className={`absolute top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${showAddTaskForm ? 'translate-x-0' : 'translate-x-full'}`}>
             <div className="flex justify-between items-center p-4 border-b bg-gray-50 sticky top-0 z-10">
              <h3 className="text-2xl font-bold text-gray-800">Add New Task</h3>
               <button onClick={() => setShowAddTaskForm(false)} className="text-gray-600 hover:text-gray-900">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <form onSubmit={handleTaskSubmit} className="h-full flex flex-col">
              <div className="p-6 space-y-6 overflow-y-auto" style={{ height: 'calc(100vh - 140px)' }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Title:</label>
                    <input type="text" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" name="title" value={newTask.title} onChange={handleTaskInputChange} required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Case:</label>
                    <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" name="case" value={newTask.case} onChange={handleTaskInputChange}>
                      <option value="">Select a Case (Optional)</option>
                      {(cases || []).map(_case => (
                        <option key={_case.id} value={_case.id}>{_case.title}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Due Date & Time:</label>
                    <input type="datetime-local" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" name="due_date" value={newTask.due_date} onChange={handleTaskInputChange} required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status:</label>
                    <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" name="status" value={newTask.status} onChange={handleTaskInputChange}>
                      {(taskStatusOptions || []).map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Priority:</label>
                    <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" name="priority" value={newTask.priority} onChange={handleTaskInputChange}>
                      {(taskPriorityOptions || []).map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Assigned To:</label>
                    <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" name="assigned_to" value={newTask.assigned_to} onChange={handleTaskInputChange}>
                      <option value="">Select a User (Optional)</option>
                      {(allowedUsers || []).map(user => (
                        <option key={user.id} value={user.id}>{user.username}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Description:</label>
                    <textarea className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" name="description" value={newTask.description} onChange={handleTaskInputChange}></textarea>
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-4 p-4 border-t bg-gray-50 sticky bottom-0">
                 <button type="button" onClick={() => setShowAddTaskForm(false)} className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300">Cancel</button>
                 <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-green-700">Add Task</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {!showOversightMode && (
      <>
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            <div className={`grid grid-cols-1 md:grid-cols-4 ${canSeeAssignmentScoping ? 'lg:grid-cols-5' : ''} gap-4`}>
                <input
                    type="text"
                    placeholder="Search tasks..."
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <select className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="">All Statuses</option>
                    {(taskStatusOptions || []).map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                <select className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
                    <option value="">All Priorities</option>
                    {(taskPriorityOptions || []).map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                {canSeeAssignmentScoping && (
                  <select className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" value={assignmentFilter} onChange={(e) => setAssignmentFilter(e.target.value)}>
                      <option value="">All Tasks</option>
                      <option value="to_me">Assigned to Me</option>
                      <option value="by_me">Assigned by Me</option>
                  </select>
                )}
                 <button className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 shadow-sm" onClick={() => { setSearchTerm(''); setStatusFilter(''); setPriorityFilter(''); setAssignmentFilter(''); }}>
                    Clear Filters
                </button>
            </div>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="p-4 border-b flex justify-between items-center">
          <h5 className="text-lg font-bold text-gray-800">All Tasks</h5>
          <span className="bg-blue-100 text-blue-800 text-xs font-semibold mr-2 px-2.5 py-0.5 rounded-full">{filteredAndSortedTasks.length} Tasks</span>
        </div>
        <div className="p-6">
            {tasksViewMode === 'board' ? (
                <div className="flex overflow-x-auto space-x-4 p-2">
                    {(taskStatusOptions || []).map(statusOption => (
                        <div key={statusOption.value} className="flex-shrink-0 w-80 bg-gray-100 rounded-lg shadow-md">
                            <div className="p-4 border-b border-gray-200">
                              <h6 className="font-bold text-center text-gray-800">{statusOption.label}</h6>
                            </div>
                            <div className="p-4 space-y-4" onDragOver={onDragOver} onDrop={(e) => onDrop(e, statusOption.value)} style={{ minHeight: '400px' }}>
                                {filteredAndSortedTasks.filter(task => task.status === statusOption.value).map(task => (
                                    <div key={task.id} className="bg-white p-4 rounded-lg shadow-sm cursor-grab" draggable={permissions.canEditTask} onDragStart={(e) => onDragStart(e, task.id)}>
                                        <h6 className="font-bold text-gray-800">{task.title}</h6>
                                        <p className="text-sm text-gray-600"><strong>Due:</strong> {formatDateForDisplay(task.due_date)}</p>
                                        <p className={`text-sm font-semibold text-${getPriorityColor(task.priority)}-600`}>Priority: {task.priority}</p>
                                        <div className="mt-4 flex gap-2">
                                          <button className="flex-1 bg-gray-200 text-gray-700 px-3 py-2 rounded-md text-sm hover:bg-gray-300 flex items-center justify-center" onClick={() => openTaskDetailsModal(task)}>
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z" /><path fillRule="evenodd" d="M.458 10C3.732 4.943 7.523 3 10 3s6.268 1.943 9.542 7c-3.274 5.057-7.03 7-9.542 7S3.732 15.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" /></svg>
                                            View
                                          </button>
                                          {permissions.canEditTask && (
                                            <button className="bg-blue-50 text-blue-700 px-3 py-2 rounded-md text-sm hover:bg-blue-100 flex items-center justify-center" onClick={() => openEditTaskModal(task)} title="Edit Task">
                                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" /><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" /></svg>
                                            </button>
                                          )}
                                          {permissions.canDeleteTask && (
                                            <button className="bg-red-50 text-red-700 px-3 py-2 rounded-md text-sm hover:bg-red-100 flex items-center justify-center" onClick={() => handleDelete('tasks', task.id, 'Task', fetchTasks)} title="Delete Task">
                                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" /></svg>
                                            </button>
                                          )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            ) : tasksViewMode === 'list' ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                       <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer" onClick={() => requestSort('title')}>Title</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer" onClick={() => requestSort('case_title')}>Case</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer" onClick={() => requestSort('due_date')}>Due Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer" onClick={() => requestSort('status')}>Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer" onClick={() => requestSort('priority')}>Priority</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer" onClick={() => requestSort('assigned_to_username')}>Assigned To</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredAndSortedTasks.map(task => (
                            <tr key={task.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{task.title}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{task.case_title || 'N/A'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDateForDisplay(task.due_date)}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{task.status}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold"><span className={`text-${getPriorityColor(task.priority)}-600`}>{task.priority}</span></td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{task.assigned_to_username || 'N/A'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-4 flex">
                                    {permissions.canEditTask && <button className="text-blue-600 hover:text-blue-900 flex items-center" onClick={() => openEditTaskModal(task)}>
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" /><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" /></svg>
                                      Edit
                                    </button>}
                                    <button className="text-gray-600 hover:text-gray-900 flex items-center" onClick={() => openTaskDetailsModal(task)}>
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z" /><path fillRule="evenodd" d="M.458 10C3.732 4.943 7.523 3 10 3s6.268 1.943 9.542 7c-3.274 5.057-7.03 7-9.542 7S3.732 15.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" /></svg>
                                      View
                                    </button>
                                    {permissions.canDeleteTask && (
                                      <button
                                        className="text-red-600 hover:text-red-900 flex items-center"
                                        onClick={() => handleDelete('tasks', task.id, 'Task', fetchTasks)}
                                        title="Delete Task"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" /></svg>
                                        Delete
                                      </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredAndSortedTasks.map(task => (
                    <div key={task.id} className="bg-white shadow-md rounded-lg overflow-hidden flex flex-col">
                        <div className="p-6 flex-grow">
                            <h5 className="text-lg font-bold text-gray-800">{task.title}</h5>
                            <p className="text-sm text-gray-600 mt-2"><strong>Case:</strong> {task.case_title || 'N/A'}</p>
                            <p className="text-sm text-gray-600"><strong>Due Date:</strong> {formatDateForDisplay(task.due_date)}</p>
                            <p className="text-sm text-gray-600"><strong>Status:</strong> {task.status}</p>
                            <p className="text-sm font-semibold"><span className={`text-${getPriorityColor(task.priority)}-600`}>Priority: {task.priority}</span></p>
                            <p className="text-sm text-gray-600"><strong>Assigned To:</strong> {task.assigned_to_username || 'N/A'}</p>
                        </div>
                        <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-4">
                           {permissions.canEditTask && <button className="text-blue-600 hover:text-blue-900 flex items-center" onClick={() => openEditTaskModal(task)}>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" /><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" /></svg>
                             Edit
                           </button>}
                           <button className="text-gray-600 hover:text-gray-900 flex items-center" onClick={() => openTaskDetailsModal(task)}>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z" /><path fillRule="evenodd" d="M.458 10C3.732 4.943 7.523 3 10 3s6.268 1.943 9.542 7c-3.274 5.057-7.03 7-9.542 7S3.732 15.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" /></svg>
                              View
                           </button>
                           {permissions.canDeleteTask && (
                             <button
                               className="text-red-600 hover:text-red-900 flex items-center"
                               onClick={() => handleDelete('tasks', task.id, 'Task', fetchTasks)}
                               title="Delete Task"
                             >
                               <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" /></svg>
                               Delete
                             </button>
                           )}
                        </div>
                    </div>
                ))}
            </div>
          )}
        </div>
      </div>
      </>
      )}

      {showOversightMode && (
        <div className="bg-white shadow-md rounded-lg overflow-hidden p-6">
          <div className="flex justify-between items-center mb-4">
            <h5 className="text-lg font-bold text-gray-800">Team Task Oversight — Tasks I've Assigned</h5>
          </div>
          {oversightLoading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Left: per-employee roster with stats */}
              <div className="space-y-3">
                {(!oversightData || (oversightData.per_user || []).length === 0) ? (
                  <div className="bg-gray-50 rounded-xl border p-6 text-center text-gray-500 text-sm">
                    You haven't assigned any completed tasks in this period.
                  </div>
                ) : (
                  oversightData.per_user.map(row => (
                    <div key={row.user_id}
                      className={`bg-white rounded-xl shadow-sm border p-4 cursor-pointer transition hover:shadow-md ${oversightSelectedEmployee?.user_id === row.user_id ? 'ring-2 ring-blue-400' : ''}`}
                      onClick={() => selectOversightEmployee(row)}>
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-gray-800">{row.name}</h4>
                        {row.overdue_count > 0 && (
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-700">{row.overdue_count} overdue</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {row.total_completed} completed &bull; avg turnaround {row.avg_turnaround_hours}h
                      </p>
                    </div>
                  ))
                )}
              </div>

              {/* Right: drill-down task list for selected employee */}
              <div className="lg:col-span-2">
                {!oversightSelectedEmployee ? (
                  <div className="bg-white rounded-xl shadow-sm border p-5 text-center text-gray-400 text-sm">
                    Select a team member to see the tasks you've assigned them.
                  </div>
                ) : (
                  <div className="bg-white rounded-xl shadow-sm border p-5 sticky top-6">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Tasks assigned to {oversightSelectedEmployee.name}</h3>
                    {oversightTasksLoading ? (
                      <div className="text-center py-6 text-gray-500 text-sm">Loading...</div>
                    ) : oversightEmployeeTasks.length === 0 ? (
                      <p className="text-sm text-gray-400">No tasks found.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {oversightEmployeeTasks.map(task => (
                              <tr key={task.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openTaskDetailsModal(task)}>
                                <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">{task.title}</td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{formatDateForDisplay(task.due_date)}</td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{task.status}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {showEditTaskModal && editingTask && (
        <div className={`fixed inset-0 bg-gray-600 bg-opacity-75 h-full w-full z-50 transition-opacity duration-300 ${showEditTaskModal ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <div className={`absolute top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${showEditTaskModal ? 'translate-x-0' : 'translate-x-full'}`}>
                <div className="flex justify-between items-center p-4 border-b bg-gray-50 sticky top-0 z-10">
                    <h5 className="text-2xl font-bold text-gray-800">Edit Task: {editingTask.title}</h5>
                    <button onClick={closeEditTaskModal} className="text-gray-600 hover:text-gray-900">
                         <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>
                <form onSubmit={handleEditTaskSubmit} className="h-full flex flex-col">
                  <div className="p-6 space-y-6 overflow-y-auto" style={{ height: 'calc(100vh - 140px)' }}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700">Title:</label>
                            <input type="text" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" name="title" value={editingTask.title || ''} onChange={handleEditingTaskChange} required />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Case:</label>
                            <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" name="case" value={editingTask.case || ''} onChange={handleEditingTaskChange}>
                                <option value="">Select a Case (Optional)</option>
                                {(cases || []).map(_case => (
                                    <option key={_case.id} value={_case.id}>{_case.title}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Due Date & Time:</label>
                            <input type="datetime-local" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" name="due_date" value={editingTask.due_date || ''} onChange={handleEditingTaskChange} required />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Status:</label>
                            <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" name="status" value={editingTask.status || ''} onChange={handleEditingTaskChange}>
                                {(taskStatusOptions || []).map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Priority:</label>
                            <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" name="priority" value={editingTask.priority || ''} onChange={handleEditingTaskChange}>
                                {(taskPriorityOptions || []).map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700">Assigned To:</label>
              <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" name="assigned_to" value={editingTask.assigned_to || ''} onChange={handleEditingTaskChange}>
                                <option value="">Select a User (Optional)</option>
                {(allowedUsers || []).map(user => (
                                    <option key={user.id} value={user.id}>{user.username}</option>
                                ))}
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700">Description:</label>
                            <textarea className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm" name="description" value={editingTask.description || ''} onChange={handleEditingTaskChange}></textarea>
                        </div>
                    </div>
                    </div>
                    <div className="flex justify-end space-x-4 p-4 border-t bg-gray-50 sticky bottom-0">
                        <button type="button" className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300" onClick={closeEditTaskModal}>Cancel</button>
                        <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-green-700">Save Changes</button>
                    </div>
                </form>
            </div>
        </div>
      )}

      {showTaskDetailsModal && selectedTaskDetails && (
        <div className={`fixed inset-0 bg-gray-600 bg-opacity-75 h-full w-full z-50 transition-opacity duration-300 ${showTaskDetailsModal ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <div className={`absolute top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${showTaskDetailsModal ? 'translate-x-0' : 'translate-x-full'}`}>
                <div className="flex justify-between items-center p-4 border-b bg-gray-50 sticky top-0 z-10">
                    <h5 className="text-2xl font-bold text-gray-800">Task Details: {selectedTaskDetails.title}</h5>
                    <button onClick={closeTaskDetailsModal} className="text-gray-600 hover:text-gray-900">
                         <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>
                <div className="p-6 space-y-4 overflow-y-auto" style={{ height: 'calc(100vh - 140px)' }}>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-md">
                        <div><strong className="font-semibold text-gray-700">Case:</strong> {selectedTaskDetails.case_title || 'N/A'}</div>
                        <div><strong className="font-semibold text-gray-700">Assigned To:</strong> {selectedTaskDetails.assigned_to_username || 'N/A'}</div>
                        <div><strong className="font-semibold text-gray-700">Due Date:</strong> {formatDateForDisplay(selectedTaskDetails.due_date)}</div>
                        <div><strong className="font-semibold text-gray-700">Status:</strong> <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800`}>{selectedTaskDetails.status}</span></div>
                        <div><strong className="font-semibold text-gray-700">Priority:</strong> <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-${getPriorityColor(selectedTaskDetails.priority)}-100 text-${getPriorityColor(selectedTaskDetails.priority)}-800`}>{selectedTaskDetails.priority}</span></div>
                        <div><strong className="font-semibold text-gray-700">Created By:</strong> {selectedTaskDetails.created_by_username || 'N/A'}</div>
                        <div className="col-span-2"><strong className="font-semibold text-gray-700">Description:</strong><p className="mt-1 p-2 bg-gray-100 rounded-md">{selectedTaskDetails.description || 'No description provided.'}</p></div>
                        {selectedTaskDetails.delay_reason && (
                          <div className="col-span-2"><strong className="font-semibold text-gray-700">Reason for Delay:</strong><p className="mt-1 p-2 bg-yellow-50 border border-yellow-200 rounded-md">{selectedTaskDetails.delay_reason}</p></div>
                        )}
                     </div>
                </div>
                <div className="flex justify-end p-4 border-t space-x-4 bg-gray-50 sticky bottom-0">
                    <button type="button" className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300" onClick={closeTaskDetailsModal}>Close</button>
                    {permissions.canEditTask && (
                     <button type="button" className="bg-blue-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-blue-700" onClick={() => {
                        openEditTaskModal(selectedTaskDetails);
                    }}>Edit Task</button>
                    )}
                    {permissions.canDeleteTask && (
                      <button
                        type="button"
                        className="bg-red-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-red-700"
                        onClick={async () => {
                          await handleDelete('tasks', selectedTaskDetails.id, 'Task', fetchTasks);
                          closeTaskDetailsModal();
                        }}
                      >
                        Delete Task
                      </button>
                    )}
                </div>
            </div>
        </div>
      )}
    </div>
  );
}

export default TaskManagement;

